import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Turnstile, { TURNSTILE_ENABLED } from "../components/Turnstile";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "", referrer: "" });
  const [refVerified, setRefVerified] = useState(false);
  const [invited, setInvited] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState("");
  const tsRef = useRef(null);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refCode = params.get("ref") || "";
  const inviteToken = params.get("invite") || "";

  useEffect(() => {
    if (inviteToken) {
      api.get(`/public/invite/${inviteToken}`)
        .then((r) => {
          setInvited(r.data);
          setForm((f) => ({ ...f, email: r.data.email, name: r.data.name || f.name, referrer: r.data.invited_by_name || "Solera" }));
        })
        .catch(() => setError("This invitation is not valid or has expired. Ask Solera for a new one."));
      return;
    }
    if (refCode) {
      api.get(`/public/referral/${refCode}`)
        .then((r) => { setForm((f) => ({ ...f, referrer: r.data.referrer_name })); setRefVerified(true); })
        .catch(() => {});
    }
  }, [refCode, inviteToken]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register", {
        ...form,
        ref_code: refVerified ? refCode : null,
        invite_token: inviteToken || null,
        turnstile_token: token,
      });
      setUser(data);
      navigate("/onboarding");
    } catch (err) {
      setError(apiError(err));
      setToken("");
      tsRef.current?.reset();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="max-w-5xl w-full mx-auto px-6 pt-8">
        <Link to="/" className="flex items-center gap-3 w-fit">
          <span className="solera-symbol" />
          <span className="wordmark text-bone text-sm">Solera</span>
          <span className="t-cap text-muted-s ml-2">solera.vc</span>
        </Link>
      </header>
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md rise" data-testid="register-panel">
          <span className="t-label">{invited ? "You are invited" : "Registration"}</span>
          <p className="t-small text-muted-s mt-3 measure">
            {invited
              ? `${invited.invited_by_name || "Solera"} invited you to Solera. Set a password to complete your membership. Nothing about you is published anywhere, at any point.`
              : "Membership is by referral. Name your referrer so we can verify the introduction. Nothing about you is published anywhere, at any point."}
          </p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <input className="input-solera" placeholder="Full name" value={form.name} onChange={set("name")} required data-testid="register-name-input" />
            <input className="input-solera" type="email" placeholder="Email" value={form.email} onChange={set("email")} required readOnly={!!invited} data-testid="register-email-input" />
            <input className="input-solera" type="password" placeholder="Password, 8 characters minimum" value={form.password} onChange={set("password")} required minLength={8} data-testid="register-password-input" />
            {!invited && (
              <input className="input-solera" placeholder="Referred by (full name)" value={form.referrer} onChange={set("referrer")} data-testid="register-referrer-input" />
            )}
            {invited && (
              <p className="t-cap text-tint" data-testid="invite-verified-note">Invitation verified for {invited.email}.</p>
            )}
            {refVerified && !invited && (
              <p className="t-cap text-tint" data-testid="referral-verified-note">Referral link verified. {form.referrer} named you.</p>
            )}
            {error && <p className="t-small status-blocked" data-testid="register-error">{error}</p>}
            <Turnstile ref={tsRef} onToken={setToken} />
            <button className="btn-primary w-full" disabled={busy || (TURNSTILE_ENABLED && !token)} data-testid="register-submit-button">
              {busy ? "Creating" : "Create account"}
            </button>
          </form>
          <p className="t-cap text-muted-s mt-8">
            Already a member? <Link to="/login" className="link-plain" data-testid="register-login-link">Login</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
